import React from 'react';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import NoRecordsFound from '../NoRecordsFound';
import {
  hoverRowStyles,
  tableCellStyleRestrictions,
  tableRowCellStyleRestrictions,
  insightsTableCell,
  insightsTableRowCell,
  tableRowCellStyle,
  tableCellStyle,
} from './tableStyles';
import { CommonPagination } from '../Pagination/CommonPagination';
import { AppDispatch, RootState } from '../../Redux/store';
import { fetchRestrictionPerDay } from '../../Redux/Slices/Auditing/auditingHandler';
import {
  updateEmailAddresses,
  updatePage,
  updateSelectedDateButton,
  setFilterRows,
  updateFilterBox,
  updateEndTime,
  updateStartTime,
} from '../../Redux/Slices/Auditing/auditing';
import { ResponsiveTypography } from '../../styles/inputLabel';
import { resetState } from '../../pages/UserInsights/userInsights';
import { statusMapping } from '../../pages/ApplicationInsights/appInsights';

interface Column {
  field: string;
  headerName: string;
  align?: 'left' | 'right' | 'center';
}

interface AuditingCommonTableProps {
  data: any[];
  columns: Column[];
  pages: 'audit-dashboard' | 'insights';
}

function ReadOnlyTable({
  data = [],
  columns,
  pages,
}: AuditingCommonTableProps) {
  const page = useSelector((state: RootState) => state.auditingDashboard.page);
  const { showFilterBox } = useSelector(
    (state: RootState) => state.auditingDashboard,
  );

  const dispatch = useDispatch<AppDispatch>();

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    const pageToUpdate = newPage;
    dispatch(updatePage(pageToUpdate + 1));
    dispatch(fetchRestrictionPerDay());
  };

  const isEmptyRecords = data.length === 0;

  const renderFieldValue = (value: any, field: string) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    if (field === 'status') {
      return statusMapping[value] || value;
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  const getTruncatedStyle = (width: number) => ({
    display: 'inline-block',
    maxWidth: `${width}px`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  });

  const navigate = useNavigate();

  const handleRowClick = (row: any) => {
    resetState(dispatch);
    dispatch(updateSelectedDateButton(30));
    dispatch(updateFilterBox(true));
    const newValue = row.user;
    const now = dayjs();
    let selectedFromDate = dayjs();
    selectedFromDate = now.subtract(1, 'month');
    const formattedStartTime = selectedFromDate.format('YYYY-MM-DD HH:mm:ss');
    const formattedEndTime = now.format('YYYY-MM-DD HH:mm:ss');

    const newFilterRow = {
      id: Date.now(),
      selectedField: 'user',
      chipList: [newValue],
      fromDate: formattedStartTime,
      toDate: formattedStartTime,
    };
    dispatch(updateStartTime(formattedStartTime));
    dispatch(updateEndTime(formattedEndTime));
    dispatch(updateEmailAddresses([newValue]));
    dispatch(setFilterRows([newFilterRow]));
    navigate('/insights');
  };

  const { cellStyle, rowCellStyle } = React.useMemo(() => {
    switch (pages) {
      case 'audit-dashboard':
        return {
          cellStyle: tableCellStyleRestrictions,
          rowCellStyle: tableRowCellStyleRestrictions,
        };
      case 'insights':
        return {
          cellStyle: insightsTableCell,
          rowCellStyle: insightsTableRowCell,
        };
      default:
        return { cellStyle: tableCellStyle, rowCellStyle: tableRowCellStyle };
    }
  }, [pages]);

  const width = pages === 'insights' ? 440 : 170;

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#ffffff',
      color: '#333333',
      maxWidth: 300,
      fontSize: '0.875rem',
      border: '0.063rem solid #e0e0e0',
      borderRadius: '0.5rem',
      boxShadow: '0 0.25rem 0.625rem rgba(0, 0, 0, 0.1)',
      padding: '0.625rem 0.938rem',
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: '#ffffff',
      boxShadow: '0 0.125rem 0.313rem rgba(0, 0, 0, 0.1)',
    },
  }));

  return (
    <Box>
      <Box
        sx={{
          height: !showFilterBox ? '40rem' : '22.5rem',
          overflowY: 'auto',
          borderBottom: '0.063rem solid lightgray',
        }}
      >
        <Table sx={{ width: '100%', borderCollapse: 'collapse' }}>
          <TableHead
            sx={{
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              boxShadow: '0px 1px 0px lightgray',
            }}
          >
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.field} sx={cellStyle}>
                  {column.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isEmptyRecords ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ borderBottom: 'none' }}
                >
                  <Box
                    sx={{
                      '& img': {
                        height: {
                          xxl: '20vh !important',
                          xl: '17vh !important',
                        },
                      },
                    }}
                  >
                    <NoRecordsFound />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              Array.isArray(data)
              && data.map((row: any, rowIndex: number) => (
                <TableRow
                  key={row.id ?? `${rowIndex}-${Math.random()}`}
                  onClick={
                    pages === 'audit-dashboard'
                      ? () => handleRowClick(row)
                      : undefined
                  }
                  sx={{
                    ...hoverRowStyles,
                    '&:hover': { backgroundColor: '#f5f5f5' },
                    cursor: 'pointer !important',
                  }}
                >
                  {columns.map((column) => (
                    <TableCell key={column.field} sx={rowCellStyle}>
                      {pages === 'audit-dashboard' ? (
                        <HtmlTooltip
                          title={
                            Array.isArray(row[column.field]) ? (
                              <Box>
                                <Typography
                                  color="inherit"
                                  sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    color: '#1976d2',
                                    borderBottom: '0.063rem solid #e0e0e0',
                                    marginBottom: '0.5rem',
                                    paddingBottom: '0.25rem',
                                    textAlign: 'center',
                                  }}
                                >
                                  {column.headerName || column.field}
                                </Typography>
                                <Box
                                  sx={{
                                    maxHeight: '12.5rem',
                                    overflowY: 'auto',
                                    paddingRight: '0.25rem',
                                  }}
                                >
                                  <ul
                                    style={{
                                      margin: 0,
                                      paddingLeft: '1.25rem',
                                      color: '#444444',
                                      fontSize: '0.938rem',
                                    }}
                                  >
                                    {row[column.field].map((item: string) => (
                                      <li
                                        key={`${
                                          row[column.field]
                                        }-${Math.random()}`}
                                        style={{
                                          marginBottom: '0.5rem',
                                          lineHeight: '1.6',
                                          color: '#2c3e50',
                                          textAlign: 'justify',
                                          fontWeight: '500',
                                          letterSpacing: '0.0.25rem',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        {item}
                                      </li>
                                    ))}
                                  </ul>
                                </Box>
                              </Box>
                            ) : (
                              <Box>
                                <ResponsiveTypography
                                  color="inherit"
                                  sx={{
                                    fontWeight: 'bold',
                                    fontSize: '1rem',
                                    color: '#1976d2',
                                    borderBottom: '0.063rem solid #e0e0e0',
                                    marginBottom: '0.5rem',
                                    paddingBottom: '0.25rem',
                                  }}
                                >
                                  {column.headerName || column.field}
                                </ResponsiveTypography>
                                <Box
                                  sx={{
                                    maxHeight: '12.5rem',
                                    overflowY: 'auto',
                                    paddingRight: '0.25rem',
                                  }}
                                >
                                  <ResponsiveTypography
                                    sx={{
                                      fontSize: '0.938rem',
                                      color: '#444444',
                                      fontWeight: '400',
                                    }}
                                  >
                                    {renderFieldValue(
                                      row[column.field],
                                      column.field,
                                    )}
                                  </ResponsiveTypography>
                                </Box>
                              </Box>
                            )
                          }
                        >
                          <span style={getTruncatedStyle(width)}>
                            {renderFieldValue(row[column.field], column.field)}
                          </span>
                        </HtmlTooltip>
                      ) : (
                        <span
                          style={getTruncatedStyle(
                            pages === 'insights' ? 200 : 170,
                          )}
                        >
                          {renderFieldValue(row[column.field], column.field)}
                        </span>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
      {pages !== 'audit-dashboard' && (
        <Box>
          <CommonPagination
            pages={pages}
            page={page}
            handlePageChange={handleChangePage}
            data={data}
          />
        </Box>
      )}
    </Box>
  );
}

export default ReadOnlyTable;
